/**
 * DEFUSE S3 — TYPED Circle adapter (`CircleEntity` → `RenderableEntity`).
 *
 * Reads the canonical typed fields off `circleEntitySchema` (model.ts:
 * `center` / `radius`) instead of the `EntityPreview.properties` string bag
 * the legacy `circleToRenderable.ts` parses. Builds the IDENTICAL parametric
 * `circle` SceneGeometry + bbox, applies `applyOffset` the same way, and
 * routes through the typed `buildRenderableShell(entity, ctx, geometry,
 * bounds)` overload (DEFUSE S2) so the produced shell is byte-for-byte the
 * same as the legacy bag path for the same entity (S3 golden-parity).
 *
 * Category B: standalone file, NOT imported into any render path yet. The
 * legacy adapter is untouched.
 *
 * Z note: the Circle model's `center` is `drawingPointSchema` ({x, y} only —
 * no z field), so the typed center z is always 0. This matches the legacy
 * path, where a Circle entity carries no `Center Z` property and `readNumber`
 * defaults it to 0. Radius is read directly: `radius` is `z.number().positive()`
 * in the model, so it is already guaranteed positive (legacy's
 * `readPositiveNumber(..., 1)` clamp is unreachable for a schema-valid entity).
 */

import type { CircleEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { buildCircleSceneGeometry } from "../sceneGeometryBuilders";

export function circleFromEntity(
  entity: CircleEntity,
  ctx: AdapterContext,
): RenderableEntity {
  const centerRaw: Vec3 = {
    x: entity.center.x,
    y: entity.center.y,
    // `center` is `drawingPointOptionalZSchema` (S-ARC-CIRCLE-CENTER-Z): the
    // legacy producer emits `Center Z` from this same field and the legacy
    // adapter reads it, so `?? 0` is the parity read (the previous hard-coded
    // 0 predated the schema z and dropped both an imported DXF group-30 z AND
    // the block-flatten z-collapse — @codex #431 round-2 Z sweep).
    z: entity.center.z ?? 0,
  };
  const radius = entity.radius;

  const { geometry, bounds } = buildCircleSceneGeometry(centerRaw, radius);

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
